import { useCallback, useMemo, useState } from 'react';
import { useMutationState } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { faBookmark, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { faBookmark as faBookmarkSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { twMerge } from 'tailwind-merge';

interface SaveForLaterButtonProps {
    disabled: boolean;
    isSaved?: boolean;
    saveForLater: () => void;
    saveForLaterMutationKey: string;
    undoSaveForLater: () => void;
    undoSaveForLaterMutationKey: string;
}

const SaveForLaterButton = ({
    disabled,
    isSaved = false,
    saveForLater,
    saveForLaterMutationKey,
    undoSaveForLater,
    undoSaveForLaterMutationKey,
}: SaveForLaterButtonProps) => {
    const saveForLaterMutations = useMutationState({
        filters: {
            mutationKey: [saveForLaterMutationKey],
        },
        select: mutation => {
            return mutation.state;
        },
    });
    const undoSaveForLaterMutations = useMutationState({
        filters: {
            mutationKey: [undoSaveForLaterMutationKey],
        },
        select: mutation => {
            return mutation.state;
        },
    });

    // NOTE: get latest saveForLater mutation
    const saveForLaterMutation = saveForLaterMutations.at(-1);
    const saveForLaterIsSuccess = saveForLaterMutation?.status === 'success';
    const saveForLaterIsPending = saveForLaterMutation?.status === 'pending';

    const undoSaveForLaterMutation = undoSaveForLaterMutations.at(-1);
    const undoSaveForLaterIsSuccess = undoSaveForLaterMutation?.status === 'success';
    const undoSaveForLaterIsPending = undoSaveForLaterMutation?.status === 'pending';

    const [isHovered, setIsHovered] = useState(false);

    const hoveredBtn = useMemo(() => {
        return {
            text: isHovered ? 'Unsave' : 'Saved',
            icon: isHovered ? faBookmark : faBookmarkSolid,
            className: isHovered ? 'text-gray-500' : 'text-green-600',
        };
    }, [isHovered]);

    const isNonInteractive =
        (saveForLaterIsSuccess && !isSaved) || (undoSaveForLaterIsSuccess && isSaved);

    const handleOnClick = useCallback(() => {
        if (isNonInteractive) return;

        if (isSaved) {
            undoSaveForLater();
            return;
        }

        saveForLater();
    }, [isNonInteractive, isSaved, saveForLater, undoSaveForLater]);

    const handleMouseInteraction = useCallback(
        ({ isHovered }: { isHovered: boolean }) => {
            if (isNonInteractive) return;

            if (isSaved) {
                setIsHovered(isHovered);
            }
        },
        [isSaved, isNonInteractive],
    );

    const ButtonText = useCallback(() => {
        if (saveForLaterIsSuccess && !isSaved) {
            return (
                <>
                    <FontAwesomeIcon className="text-green-600" icon={faBookmarkSolid} />
                    <span className="text-green-600">Saved</span>
                </>
            );
        } else if (undoSaveForLaterIsSuccess && isSaved) {
            return (
                <>
                    <FontAwesomeIcon className="text-gray-500" icon={faBookmark} />
                    <span>Unsaved</span>
                </>
            );
        } else if (isSaved) {
            if (undoSaveForLaterIsPending) {
                return (
                    <>
                        <FontAwesomeIcon className={'animate-spin'} icon={faSpinnerThird} />
                        <span className={'text-gray-500'}>{'Unsaving'}</span>
                    </>
                );
            }

            return (
                <>
                    <FontAwesomeIcon className={hoveredBtn.className} icon={hoveredBtn.icon} />
                    <span className={hoveredBtn.className}>{hoveredBtn.text}</span>
                </>
            );
        }

        return (
            <>
                <FontAwesomeIcon
                    className={saveForLaterIsPending ? 'animate-spin' : 'text-gray-500'}
                    icon={saveForLaterIsPending ? faSpinnerThird : faBookmark}
                />
                <span>{saveForLaterIsPending ? 'Saving' : 'Save for later'}</span>
            </>
        );
    }, [
        isSaved,
        saveForLaterIsSuccess,
        saveForLaterIsPending,
        undoSaveForLaterIsSuccess,
        undoSaveForLaterIsPending,
        hoveredBtn.icon,
        hoveredBtn.text,
        hoveredBtn.className,
    ]);

    return (
        <Button
            variant="none"
            color="neutral"
            size="sm"
            disabled={disabled || saveForLaterIsPending}
            className={twMerge(
                'my-2 mr-3 w-auto text-gray-500',
                isNonInteractive && 'pointer-events-none',
            )}
            onClick={handleOnClick}
            onMouseEnter={() => {
                handleMouseInteraction({ isHovered: true });
            }}
            onMouseLeave={() => {
                handleMouseInteraction({ isHovered: false });
            }}
        >
            <ButtonText />
        </Button>
    );
};

export { SaveForLaterButton };
